import React from "react";
import { Box, Typography, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            padding: 4,
            textAlign: "center",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "#ff6b6b", marginBottom: 2 }} />
          <Typography variant="h5" sx={{ color: "var(--text-primary)", marginBottom: 1 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ color: "#6E7191", marginBottom: 3, maxWidth: 400 }}>
            We encountered an unexpected error. Please try refreshing the page or go back to the
            previous screen.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={this.handleGoBack}
              sx={{
                borderColor: "#FC6634",
                color: "#FC6634",
                "&:hover": {
                  borderColor: "#e55a2d",
                  backgroundColor: "rgba(252, 102, 52, 0.04)",
                },
              }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              onClick={this.handleReload}
              sx={{
                backgroundColor: "#FC6634",
                "&:hover": {
                  backgroundColor: "#e55a2d",
                },
              }}
            >
              Refresh Page
            </Button>
          </Box>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <Box
              sx={{
                marginTop: 4,
                padding: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                maxWidth: "100%",
                overflow: "auto",
                textAlign: "left",
              }}
            >
              <Typography variant="caption" sx={{ color: "#666", fontFamily: "monospace" }}>
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
